import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Minus, Plus } from "lucide-react";
import type { BookingSheetFormProps } from "./types";

function Stepper({ value, onChange, min = 1, max = 99, label }: { value: number; onChange: (v: number) => void; min?: number; max?: number; label?: string }) {
  return (
    <div className="flex items-center gap-2">
      {label && <span className="text-sm text-muted-foreground mr-1">{label}</span>}
      <Button type="button" variant="outline" size="icon" className="h-7 w-7" onClick={() => onChange(Math.max(min, value - 1))} disabled={value <= min}>
        <Minus className="h-3 w-3" />
      </Button>
      <span className="w-8 text-center text-sm font-medium">{value}</span>
      <Button type="button" variant="outline" size="icon" className="h-7 w-7" onClick={() => onChange(Math.min(max, value + 1))} disabled={value >= max}>
        <Plus className="h-3 w-3" />
      </Button>
    </div>
  );
}

interface BookingSheetTournamentFormProps extends BookingSheetFormProps {
  paymentMethods: { value: string; label: string }[];
}

export default function BookingSheetTournamentForm({
  formState,
  onFieldChange,
  paymentMethods,
}: BookingSheetTournamentFormProps) {
  const { t } = useTranslation("admin");
  const {
    tournamentFormat,
    tournamentName,
    tournamentPlayers,
    tournamentPoints,
    tournamentPrice,
    paymentMethod,
    privateTournament,
    membersOnly,
  } = formState;

  return (
    <div className="space-y-3 border-t border-border pt-3">
      <div>
        <Label className="text-xs">{t("bookingSheet.tournamentFormat")}</Label>
        <Select value={tournamentFormat} onValueChange={(v) => onFieldChange("tournamentFormat", v)}>
          <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="americano">Americano</SelectItem>
            <SelectItem value="mexicano">Mexicano</SelectItem>
            <SelectItem value="knockout">Knockout</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label className="text-xs">{t("bookingSheet.tournamentName")}</Label>
        <Input value={tournamentName} onChange={(e) => onFieldChange("tournamentName", e.target.value)} className="h-9 text-sm" placeholder={t("bookingSheet.tournamentNamePlaceholder")} />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">{t("bookingSheet.playerCount")}</Label>
          <Stepper value={tournamentPlayers} onChange={(v) => onFieldChange("tournamentPlayers", v)} min={4} max={64} />
        </div>
        <div>
          <Label className="text-xs">{t("bookingSheet.pointCount")}</Label>
          <Stepper value={tournamentPoints} onChange={(v) => onFieldChange("tournamentPoints", v)} min={8} max={64} />
        </div>
      </div>

      <div>
        <Label className="text-xs">{t("bookingSheet.pricePerPerson")}</Label>
        <Input value={tournamentPrice} onChange={(e) => onFieldChange("tournamentPrice", e.target.value)} className="h-9 text-sm" placeholder="0" />
      </div>

      <div>
        <Label className="text-xs">{t("bookingSheet.payment")}</Label>
        <Select value={paymentMethod} onValueChange={(v) => onFieldChange("paymentMethod", v)}>
          <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            {paymentMethods.map(m => (
              <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center justify-between">
        <Label className="text-xs">{t("bookingSheet.privateTournament")}</Label>
        <Switch checked={privateTournament} onCheckedChange={(v) => onFieldChange("privateTournament", v)} />
      </div>

      <div className="flex items-center justify-between">
        <Label className="text-xs">{t("bookingSheet.membersOnly")}</Label>
        <Switch checked={membersOnly} onCheckedChange={(v) => onFieldChange("membersOnly", v)} />
      </div>
    </div>
  );
}
